import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { TicketAppointmentStateDto } from "@/api/types";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

import type { AdminTicketTabs, ResidentTicketTabs } from "./constants";

type TabsStatsQuery = ApiQueryParams<"getAdminTicketsStatsAllTabsV1">;

// the repair provider appointment planning is an async server-side job. Mirror the iOS client:
// poll the planning for proposed appointment options at a fixed interval, capped, and stop once
// the planning reaches a state that no longer changes on its own.
export const APPOINTMENT_PLANNING_POLL_INTERVAL_MS = 5000;
// Preparation can include a 60-second provider request and retries.
export const APPOINTMENT_PLANNING_MAX_POLL_ATTEMPTS = 37;
const APPOINTMENT_PLANNING_TERMINAL_STATES: TicketAppointmentStateDto[] = [
  "awaitingResidentSelection",
  "manualFollowUp",
  "confirmed",
  "appointmentChanged",
  "failed",
];
// States that are still resolving on the server, so the ticket-detail card keeps polling for them.
const APPOINTMENT_STATUS_PENDING_STATES: TicketAppointmentStateDto[] = ["submitted", "fetchingAppointments"];

export type TicketsFilters = Omit<ApiQueryParams<"getAdminTicketsV1">, "Limit" | "Offset" | "SortBy">;

export const useTicketQueries = () => {
  const api = useApi();
  const projectId = useProjectId();

  return {
    listForResident: (query: ApiQueryParams<"getTicketsV1">) => {
      const limit = 30;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.TICKETS_RESIDENT_FEED(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getTicketsV1({
              Limit: limit,
              Offset: pageParam * limit,
              ...query,
            })
            .then(commonAPIDataSelector),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    listForAdmin: (query: ApiQueryParams<"getAdminTicketsV1">) => {
      const limit = 30;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.TICKETS_FEED(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getAdminTicketsV1({
              Limit: limit,
              Offset: pageParam * limit,
              ...query,
            })
            .then(commonAPIDataSelector),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    detailsAdmin: (ticketId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, ticketId),
        queryFn: () => api.getAdminTicketsDetailsV1(ticketId),
        select: commonAPIDataSelector,
      }),
    appointment: (ticketId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_DETAILS_APPOINTMENT(projectId, ticketId),
        queryFn: () => api.getTicketsAppointmentV1(ticketId),
        select: commonAPIDataSelector,
        retry: false,
        // Keep the ticket-detail card live while the appointment options are still being fetched.
        refetchInterval: (query) => {
          const state = query.state.data?.data?.state;

          if (state && APPOINTMENT_STATUS_PENDING_STATES.includes(state)) {
            return APPOINTMENT_PLANNING_POLL_INTERVAL_MS;
          }

          return false;
        },
      }),
    // Poll a pre-ticket appointment planning for the proposed moments (used in the create-ticket modal).
    appointmentPlanning: (planningId: string | undefined) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_APPOINTMENT_PLANNING(projectId, planningId ?? ""),
        queryFn: () => api.getTicketsAppointmentPlanningsDetailsV1(planningId!),
        select: commonAPIDataSelector,
        enabled: Boolean(planningId),
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchInterval: (query) => {
          const state = query.state.data?.data?.state;

          if (state && APPOINTMENT_PLANNING_TERMINAL_STATES.includes(state)) {
            return false;
          }

          // Count failed polls too: with `retry: false` a persistently erroring planning never
          // bumps `dataUpdateCount`, so a data-only cap would poll forever.
          if (query.state.dataUpdateCount + query.state.errorUpdateCount >= APPOINTMENT_PLANNING_MAX_POLL_ATTEMPTS) {
            return false;
          }

          return APPOINTMENT_PLANNING_POLL_INTERVAL_MS;
        },
      }),
    activitiesInfinite: ({ ticketId, sortDescending }: { ticketId: string; sortDescending?: boolean }) => {
      const limit = sortDescending ? 20 : 1000;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.TICKETS_DETAILS_ACTIVITIES(projectId, ticketId, {
          sortDescending,
        }),
        queryFn: ({ pageParam }) =>
          api
            .getAdminTicketsActivitiesV1(ticketId, {
              Offset: pageParam * limit,
              Limit: limit,
              sortDescending,
            })
            .then((x) => x.data),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    responsibilitySuggestion: (ticketId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_RESPONSIBILITY_SUGGESTION(projectId, ticketId),
        queryFn: () => api.getAdminTicketsResponsibilitySuggestionV1(ticketId),
        select: commonAPIDataSelector,
        retry: false,
        staleTime: Infinity,
      }),
    denialReplySuggestion: (ticketId: string, data: { additionalInfo?: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_DENIAL_REPLY_SUGGESTION(projectId, ticketId),
        queryFn: () => api.postTicketsSuggestionV1(ticketId, data),
        select: commonAPIDataSelector,
        staleTime: 30 * 60 * 1000,
      }),
    ratingFeedbackOptions: () =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKET_RATING_FEEDBACK_OPTIONS(projectId),
        queryFn: () => api.getTicketsFeedbackOptionsV1(),
        select: commonAPIDataSelector,
        staleTime: Infinity,
      }),
    ticketTranslation: ({ ticketId, languageId }: { ticketId: string; languageId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_DETAILS_TRANSLATION(projectId, ticketId, languageId),
        queryFn: () => api.getTicketsTranslationsDetailsV1(ticketId, languageId),
        select: commonAPIDataSelector,
        retry: false,
      }),
    activityTranslation: ({
      ticketId,
      commentId,
      languageId,
    }: {
      ticketId: string;
      commentId: string;
      languageId: string;
    }) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKET_COMMENT_TRANSLATION(projectId, ticketId, commentId, languageId),
        queryFn: () => api.getTicketsCommentsTranslationsDetailsV1(ticketId, commentId, languageId),
        select: commonAPIDataSelector,
        retry: false,
      }),
    detailsResident: (ticketId: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_RESIDENT_DETAILS(projectId, ticketId),
        queryFn: () => api.getTicketsDetailsV1(ticketId),
        select: commonAPIDataSelector,
      }),
    commentsResident: (ticketId: string) => {
      const limit = 20;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.TICKETS_RESIDENT_DETAILS_COMMENTS(projectId, ticketId),
        queryFn: ({ pageParam }) =>
          api
            .getTicketsCommentsV1(ticketId, {
              Offset: pageParam * limit,
              Limit: limit,
            })
            .then((x) => x.data),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    tabsStats: (query: TabsStatsQuery) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_TABS_AMOUNT(projectId, query),
        queryFn: () => api.getAdminTicketsStatsAllTabsV1(query),
        select: commonAPIDataSelector,
      }),
    totalTicketsInTab: (tab: AdminTicketTabs) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_TAB_TOTAL(projectId, tab),
        queryFn: () => api.getAdminTicketsStatsDetailsV1(tab),
        select: commonAPIDataSelector,
      }),
    filterOptions: () =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_FILTERS_VALUES(projectId),
        queryFn: () => api.getAdminTicketsFiltersV1(),
        select: commonAPIDataSelector,
      }),
    residentTabsStats: (
      query: Omit<ApiQueryParams<"getTicketsV1">, "Search" | "CategoryIds" | "RepairRequestFilterType" | "StatusIds">,
    ) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_RESIDENT_TABS_AMOUNT(projectId, query),
        queryFn: () => api.getTicketsStatsAllStatusesV1(query),
        select: commonAPIDataSelector,
      }),
    residentTabTotal: (tab: ResidentTicketTabs) =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_RESIDENT_TAB_TOTAL(projectId, tab),
        queryFn: () => api.getTicketsStatsDetailsV1(tab),
        select: commonAPIDataSelector,
      }),
    residentFilterOptions: () =>
      queryOptions({
        queryKey: QUERY_KEYS.TICKETS_FILTERS_VALUES(projectId),
        queryFn: () => api.getTicketsFiltersV1(),
        select: commonAPIDataSelector,
      }),
    historyInfinite: ({ ticketId, historyType }: { ticketId: string; historyType: "addresses" | "users" }) => {
      const limit = 5;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.TICKETS_HISTORY(projectId, ticketId, historyType),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getTicketsHistoryDetailsV1(ticketId, historyType, {
              Limit: limit,
              Offset: pageParam * limit,
            })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.history.hasMore ? pages?.length : undefined),
      });
    },
  };
};
